import classNames from 'classnames';
import React, {
  JSX,
  memo,
  MouseEventHandler,
  PropsWithChildren,
  ReactEventHandler,
  LegacyRef,
  forwardRef,
} from 'react';

import { MenuItemStyle, TMenuItemSize } from '../MenuItem/MenuItem.types';
import { TUITheme } from '../types';
import styles from './MenuItem.styles.m.css';

export interface IMenuItemProps {
  title: string;
  icon?: JSX.Element;
  /**
   * @deprecated use suffix instead
   */
  iconSuffix?: JSX.Element;
  suffix?: JSX.Element;
  onClick?: ReactEventHandler<HTMLButtonElement>;
  onMouseEnter?: MouseEventHandler<HTMLButtonElement>;
  onMouseLeave?: MouseEventHandler<HTMLButtonElement>;
  theme?: TUITheme;
  style?: MenuItemStyle;
  className?: string;
  titleClassName?: string;
  isDisabled?: boolean;
  dataTest?: string;
  withDot?: boolean;
  /**
   * @deprecated use value instead
   */
  count?: number | string;
  value?: number | string;
  isSelected?: boolean;
  size?: TMenuItemSize;
}

export const MenuItem = memo(
  forwardRef<HTMLElement, PropsWithChildren<IMenuItemProps>>((props, ref) => {
    const {
      title,
      icon,
      iconSuffix,
      suffix,
      onClick,
      onMouseEnter,
      onMouseLeave,
      theme,
      style,
      className,
      titleClassName,
      children,
      isDisabled,
      dataTest,
      withDot,
      value = props.count,
      isSelected,
      size = 'small-1',
    } = props;

    const handleMouseDown: MouseEventHandler<HTMLButtonElement> = (e) => {
      e.preventDefault();
    };

    const containerClassName = classNames(
      styles.container,
      className,
      styles[`${theme}-theme`],
      styles[`${style}-style`],
      styles[`${size}-size`],
      {
        [styles.selected]: isSelected,
      }
    );

    return (
      <button
        className={containerClassName}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseDown={handleMouseDown}
        disabled={isDisabled}
        data-test={dataTest}
        ref={ref as LegacyRef<HTMLButtonElement>}
      >
        <span className={styles.content}>
          {icon && (
            <span className={styles.icon}>
              {icon}
              {withDot && (
                <div className={styles.dotContainer}>
                  <div className={styles.dot} />
                </div>
              )}
            </span>
          )}
          <span
            className={classNames(styles.title, titleClassName)}
            title={title}
          >
            {title}
          </span>
          {children}
          {value !== undefined && <span className={styles.value}>{value}</span>}
        </span>
        {iconSuffix && <span className={styles.iconSuffix}>{iconSuffix}</span>}
        {suffix && <span className={styles.suffix}>{suffix}</span>}
      </button>
    );
  })
);
